import React from 'react';
import {AlertTriangleIcon, XIcon} from '../Icons';

export type ConversionScope = 'project' | 'all';

interface ConversionsToolbarProps {
  projectName: string | null;
  scope: ConversionScope;
  onScopeChange: (scope: ConversionScope) => void;
  /** The scope switch only means something when a project is in the URL. */
  canScope: boolean;
  error: string | null;
  onDismissError: () => void;
}

/**
 * Title, and which half of the history you are looking at.
 *
 * The desktop-era page had a project `<select>` *and* a "show all" button that did the same thing,
 * which left two controls disagreeing about what was on screen. There are only two answers here —
 * this project or everything — so it is a segmented control, the same one the word lists use.
 */
const ConversionsToolbar: React.FC<ConversionsToolbarProps> = ({
  projectName,
  scope,
  onScopeChange,
  canScope,
  error,
  onDismissError,
}) => (
  <>
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold text-gray-50">Conversions</h1>
        <p className="mt-0.5 text-sm text-gray-500">
          Every Final Cut Pro XML you have produced. Converting again costs nothing, so keep the ones
          you use and delete the rest.
        </p>
      </div>
    </div>

    {error && (
      <div className="flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/[0.06]
       px-3 py-2.5 text-sm text-red-200">
        <AlertTriangleIcon className="mt-0.5 w-4 h-4 shrink-0" />
        <p className="flex-1">{error}</p>
        <button
          type="button"
          onClick={onDismissError}
          aria-label="Dismiss error"
          className="shrink-0 text-red-300/70 hover:text-red-200 cursor-pointer"
        >
          <XIcon className="w-4 h-4" />
        </button>
      </div>
    )}

    {canScope && (
      <div className="inline-flex rounded-lg border border-gray-800 bg-gray-900/60 p-0.5">
        <ScopeButton
          label={projectName || 'This project'}
          isActive={scope === 'project'}
          onClick={() => onScopeChange('project')}
        />
        <ScopeButton
          label="All projects"
          isActive={scope === 'all'}
          onClick={() => onScopeChange('all')}
        />
      </div>
    )}
  </>
);

const ScopeButton: React.FC<{label: string; isActive: boolean; onClick: () => void}> = ({
  label,
  isActive,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isActive}
    className={`max-w-[16rem] truncate rounded-md px-3 py-1.5 text-xs font-medium transition-colors
     cursor-pointer ${isActive ? 'bg-gray-800 text-gray-100' : 'text-gray-500 hover:text-gray-300'}`}
  >
    {label}
  </button>
);

interface ConversionsSummaryProps {
  total: number;
  censoredWords: number;
  totalCuts: number;
}

/** Three numbers, flat and neutral — the old build gave each one its own colour and a card. */
export const ConversionsSummary: React.FC<ConversionsSummaryProps> = ({
  total,
  censoredWords,
  totalCuts,
}) => (
  <div className="grid grid-cols-3 gap-3">
    <Stat label="Conversions" value={total} />
    <Stat label="Words censored" value={censoredWords} />
    <Stat label="Cuts made" value={totalCuts} />
  </div>
);

const Stat: React.FC<{label: string; value: number}> = ({label, value}) => (
  <div className="rounded-xl border border-gray-800 bg-gray-900/60 px-4 py-3">
    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-lg font-semibold tabular-nums text-gray-100">{value.toLocaleString()}</p>
  </div>
);

export default ConversionsToolbar;
